import React, { useEffect } from 'react'
import { useConfig } from '@peracto/peracto-config'
import TextInput from '@peracto/peracto-editor-ui/dist/TextInput'
import File from '@peracto/peracto-editor-ui/dist/File'
import Select from '@peracto/peracto-editor-ui/dist/Select'
import Checkbox from '@peracto/peracto-editor-ui/dist/Checkbox'
import Slider from '@peracto/peracto-editor-ui/dist/Slider'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'

import startCase from 'lodash/startCase'

const CarouselForm = ({
    onChange,
    state: {
        slideDuration = 5000,
        transition = 'slide',
        transitionDuration = 1000,
        autoplay = false,
        pauseOnHover = true,
        showArrows = true,
        arrowsColour = '#bababa',
        showIndicators = false,
        indicatorsPosition = 'below',
        indicatorsColour = '#000',
        images = [],
        selectedImageIndex,
    },
}) => {
    const DEFAULT_IMAGE = {
        url: 'https://via.placeholder.com/600x200',
        openInNewTab: false,
    }

    const config = useConfig()
    const { theme } = config.get('editorConfig') || []

    const themeColours = []

    if (theme?.colors?.brand) {
        for (const [label, colour] of Object.entries(theme.colors.brand)) {
            themeColours.push({
                label: startCase(label),
                value: colour,
            })
        }
    }

    useEffect(() => {
        return () => {
            onChange({ selectedImageIndex: 0 })
        }
    }, [])

    return (
        <>
            <Select
                label="Transition Style"
                options={[
                    { label: 'Slide', value: 'slide' },
                    { label: 'Fade', value: 'fade' },
                ]}
                value={transition}
                onChange={e => onChange({ transition: e.value })}
            />

            <Slider
                label={`Transition Speed: ${transitionDuration / 1000}s`}
                min={0}
                max={10000}
                step={100}
                value={transitionDuration}
                onChange={e => onChange({ transitionDuration: e.target.value })}
            />

            <Checkbox
                label="Autoplay"
                checked={autoplay}
                onChange={e => onChange({ autoplay: e.target.checked })}
            />

            {autoplay && (
                <>
                    <Slider
                        label={`Slide Duration: ${slideDuration / 1000}s`}
                        min={0}
                        max={10000}
                        step={100}
                        value={slideDuration}
                        onChange={e => onChange({ slideDuration: e.target.value })}
                    />

                    <Checkbox
                        label="Pause on Hover"
                        checked={pauseOnHover}
                        onChange={e => onChange({ pauseOnHover: e.target.checked })}
                    />
                </>
            )}

            <Checkbox
                label="Show Arrows"
                checked={showArrows}
                onChange={e => onChange({ showArrows: e.target.checked })}
            />

            {showArrows && (
                <Select
                    label="Arrows Colour"
                    options={themeColours}
                    value={arrowsColour}
                    onChange={e => onChange({ arrowsColour: e.value })}
                />
            )}

            <Checkbox
                label="Show Indicators"
                checked={showIndicators}
                onChange={e => onChange({ showIndicators: e.target.checked })}
            />

            {showIndicators && (
                <>
                    <Select
                        label="Indicators Position"
                        options={[
                            { label: 'Below', value: 'below' },
                            { label: 'Overlay', value: 'overlay' },
                        ]}
                        value={indicatorsPosition}
                        onChange={e => onChange({ indicatorsPosition: e.value })}
                    />

                    <Select
                        label="Indicators Colour"
                        options={themeColours}
                        value={indicatorsColour}
                        onChange={e => onChange({ indicatorsColour: e.value })}
                    />
                </>
            )}

            <hr />

            {selectedImageIndex !== null && images && images[selectedImageIndex] && (
                <>
                    <File
                        label="Image"
                        onChange={value => {
                            const imagesClone = [...images]
                            imagesClone[selectedImageIndex].url = value
                            onChange({
                                images: imagesClone,
                            })
                        }}
                        value={images[selectedImageIndex].url || ''}
                        isInline={false}
                    />

                    <File
                        label="Mobile Image"
                        onChange={value => {
                            const imagesClone = [...images]
                            imagesClone[selectedImageIndex].mobileUrl = value
                            onChange({
                                images: imagesClone,
                            })
                        }}
                        value={images[selectedImageIndex].mobileUrl || ''}
                        isInline={false}
                    />

                    <TextInput
                        label="Image alt text"
                        onChange={e => {
                            const imagesClone = [...images]
                            imagesClone[selectedImageIndex].alt = e.target.value

                            onChange({
                                images: imagesClone,
                            })
                        }}
                        value={images[selectedImageIndex].alt || ''}
                    />

                    <hr />

                    <TextInput
                        label="Image link"
                        onChange={e => {
                            const imagesClone = [...images]
                            imagesClone[selectedImageIndex].link = e.target.value

                            onChange({
                                images: imagesClone,
                            })
                        }}
                        value={images[selectedImageIndex].link || ''}
                    />

                    <Checkbox
                        label="Open in new tab"
                        checked={images[selectedImageIndex].openInNewTab}
                        onChange={e => {
                            const imagesClone = [...images]
                            imagesClone[selectedImageIndex].openInNewTab = e.target.checked
                            onChange({
                                images: imagesClone,
                            })
                        }}
                    />

                    <button
                        className="btn btn-sm btn-danger w-100 mt-2"
                        onClick={e => {
                            const imagesClone = [...images]

                            imagesClone.splice(selectedImageIndex, 1)

                            onChange({
                                images: imagesClone,
                            })
                            onChange({
                                selectedImageIndex:
                                    selectedImageIndex > 0 ? selectedImageIndex - 1 : 0,
                            })
                        }}
                    >
                        <FontAwesomeIcon icon={faTrash} className="mr-2" /> Remove Image
                    </button>
                </>
            )}

            <hr className="my-2" />

            <button
                className="btn btn-sm btn-secondary w-100"
                onClick={e => {
                    const imagesClone = [...images, DEFAULT_IMAGE]
                    onChange({
                        images: imagesClone,
                    })
                    onChange({
                        selectedImageIndex: imagesClone.length - 1,
                    })
                }}
            >
                <FontAwesomeIcon icon={faPlusCircle} className="mr-2" /> Add Image
            </button>
        </>
    )
}

export default CarouselForm
